<template>
  <aside class="modal" v-if="active" :class="{'is-active': active}">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <span class="modal-card-title is-flex">
          <h2 v-if="!isJudgeTeamProject">{{ $t('teamModal.edit') }}</h2>
          <h2 v-if="isJudgeTeamProject">
            <span v-if="currentUser.isJudgingAdmin">Edit Judge Meeting Groups</span>
            <span v-if="!currentUser.isJudgingAdmin">View Meeting Groups</span>
          </h2>
        </span>
        <span v-if="hasUnsavedChanges && attemptClose" class="confirm-close help has-text-danger">{{ $t('teamModal.unsaved') }}</span>
        <button class="delete" @mousedown="close" aria-label="To close edit-member modal" role="button"></button>
      </header>
      <section v-if="viewMode === teamManagerView" class="modal-card-body" :style="{ '--overflow-value': autoOrVisible}">
        <infinite-scroll :callback="fetchMembers">
          <ul>
            <li class="columns team-member is-flex" v-for="(member, index) in localMembers" :key="member.user.uuid">
              <div class="column is-one-third label-container" :data-balloon="member.user.email" data-balloon-pos="right">
                <span class="avatar"><img v-if="member.user.avatar" :src="thumbnail(member.user.avatar)" :alt="(member.user.userName)"></span>
                <p class="label has-text-left">&nbsp;&nbsp;{{member.user.userName}}</p>
              </div>
              <div v-if="!isJudgeTeamProject" class="column select control">
                <label :for="(member.user.userName + '-member-role')" class="visually-hidden">{{ $t('teamModal.memberRole') }}</label>
                <select @change="updateRole(member)" v-model="member.role" :disabled="!canEditRole(member)" :id="(member.user.userName + '-member-role')">
                  <option v-for="option in roleOptions" :key="option.name" :value="option.value">{{option.name}}</option>
                  <option v-if="member.role === 'owner'" value="owner">{{ $t('teamModal.owner') }}</option>
                </select>
              </div>
              <div v-if="!isJudgeTeamProject" class="input-container control column is-hidden-touch is-flex">
                <label :for="(member.user.userName + '-custom-role')" class="visually-hidden">{{ $t('teamModal.customRole') }}</label>
                <input class="input" type="text" :placeholder="customRolePlaceholder" v-model.trim="member.customRole" @keyup="updateCustomRole(member)" maxlength=25 :disabled="processing || !canEdit" :id="(member.user.userName + '-custom-role')"/>
              </div>
              <div v-if="!isJudgeTeamProject" :class="{'empty-block': member.role === 'owner' || member.role === 'resultsViewer'}"></div>
              <button v-if="!isJudgeTeamProject && member.user.id === currentUser.id || hasMembershipEditPermissions" class="remove column" @click="remove(member, index)" @keyup.enter="remove(member, index)" :disabled="processing || member.role === 'owner' || member.role === 'resultsViewer'" :class="{'is-hidden': member.role === 'owner' || member.role === 'resultsViewer'}" aria-label="To Remove Member From Group" role="button">
                  <i class="fal fa-trash-alt" aria-hidden="true" qa="delete_team_member_btn"></i>
              </button>
              <span v-if="isJudgeTeamProject">
                <div class="column select control">
                  <label :for="(member.user.userName + '-group-assignment')" class="visually-hidden">member.group</label>
                  <select @change="updateGroup(member)" v-model="member.group" :disabled="!canEditGroup(member)" :id="(member.user.userName + '-member-group')">
                    <option v-for="groupName in groupOptions" :key="groupName" :value="groupName">{{groupName}}</option>
                  </select>
                </div>
              </span>
            </li>
            <li class="columns is-flex team-member" v-for="(member, index) in membersToAdd" :key="index">
              <div v-if="!member.user" class="column is-one-third control label-container is-flex">
                <label :for="('new-member-name-' + index)" class="visually-hidden">Member Username or Email</label>
                <input v-focus class="input" ref="input" type="text" :placeholder="usernameOrEmailPlaceholder" v-model.trim="member.nameOrEmail" :disabled="processing" @blur="member.nameOrEmail === '' ? removeRow(index) : ''" :id="('new-member-name-' + index)" @keyup="searchForUsers(member.nameOrEmail, index), checkNameOrEmail(member.nameOrEmail)"/>
                <drop-down v-if="searchUsers.length > 0 && searchingAt === index" class="select-user-dropdown" :isHeader="false" :title="''" :users="searchUsers" :isUserSearch="true" @selectUser="selectUser"></drop-down>
              </div>
              <!-- Added Member Profile -->
              <div v-if="member.user" class="column is-one-third label-container">
                <span class="avatar is-hidden-mobile"><img v-if="member.user.avatar" :src="thumbnail(member.user.avatar)" :alt="(member.user.userName)"></span>
                <p class="label has-text-left">&nbsp;&nbsp;{{member.user.userName}}</p>
              </div>
              <div class="select column control">
                <label :for="('new-member-role-' + index)" class="visually-hidden">{{ $t('teamModal.memberRole') }}</label>
                <select v-model="member.role" :disabled="processing || member.nameOrEmail === ''" :id="('new-member-role-' + index)">
                  <option v-for="(option, index) in roleOptions" :key="index" :value="option.value"> {{option.name}} </option>
                </select>
              </div>
              <div class="input-container column is-hidden-touch control is-flex">
                <label :for="('new-custom-role-' + index)" class="visually-hidden">{{ $t('teamModal.customRole') }}</label>
                <input class="input" type="text" :placeholder="customRolePlaceholder" v-model.trim="member.customRole" maxlength=25 :disabled="processing || member.nameOrEmail === ''" id="('new-custom-role-' + index)"/>
                <!-- added disabled select element to fix chrome/safari bug where clicking steals focus -->
                <div class="select" :class="member.nameOrEmail === '' ? 'disabled-input' : 'hide'">
                  <select disabled></select>
                </div>
              </div>
              <button class="remove column" @click="removeAddedMember(index)" :disabled="processing" aria-label="To Remove Member From Group" role="button">
                  <i class="fal fa-trash-alt" aria-hidden="true" qa="delete_team_member_btn"></i>
              </button>
            </li>
          </ul>
        </infinite-scroll>
        <div v-if="loading && hasMore" class="loader is-centered"></div>
      </section>
      <section v-if="isBulk" class="modal-card-body" :style="{ '--overflow-value': autoOrVisible}">
        <label class="label">Bulk add users by email address <span class="help">(Separate by space, comma, or newline)</span></label>
        <textarea class="textarea" v-model.trim="bulkAddEmails"></textarea>
      </section>
      <footer v-if="canDeleteOwnAccount || hasMembershipEditPermissions || (currentUser.isJudgingAdmin && isJudgeTeamProject)" class="modal-card-foot footer-buttons is-flex">
        <div v-if="canEdit" class="footer-add-button is-flex">
          <span v-if="hasMembershipEditPermissions && isTeamManager && !isJudgeTeamProject" class="is-flex">
            <button class="add-button" @mousedown="add" tabindex="0" @keypress.enter.space="add" :disabled="!canEditRole(null)" aria-label="To add a new row" qa="new_row_btn">
              <i class="fas fa-user-plus" aria-hidden="true" />
            </button>
            <label v-if="canEditRole(null)" class="add-button-label" aria-hidden="true" @click="add"> {{ addAnotherRow }} </label>
          </span>
          <button v-if="canBulkAdd && !isJudgeTeamProject"
                  class="bulk-add cursor-pointer is-flex"
                  tabindex="0"
                  @keypress.enter.space="teamMemberViewButtonClicked"
                  @click="teamMemberViewButtonClicked">
              <i :class="{'fas fa-users-medical fa-2x' : isTeamManager,
                          'fas fa-users-cog fa-2x' :isBulk }" />
            <label class="add-button-label"
                   aria-hidden="true">
              {{ addUsersOrSeeTeam }}
            </label>
          </button>
          <div v-if="processing" class="has-text-primary is-flex"><span class="loader"></span>&nbsp;&nbsp;{{numProcessing}} {{$t('chatTemplate.members')}}.</div>
          <div v-if="errorMessage != ''" class="help has-text-danger inline-padding">{{errorMessage}}.</div>
          <div v-if="infoMessage != '' && !processing" class="has-text-primary info-message inline-padding">{{infoMessage}}.</div>
        </div>
        <button v-if="canEdit" class="button is-primary" @click="checkSaveChanges" tabindex="0" @keypress.enter.space="checkSaveChanges" :disabled="isSaveDisabled" role="button" qa="save_team_btn">{{ $t('save') }}</button>
      </footer>
    </div>
  </aside>
</template>

<script>
import Vue from 'vue'

import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import DropDown from '@/components/Controls/MetaCountsDropdown'
import dropDownMixin from '@/components/Controls/MetaCountsDropdown/mixin'

import breakpoints from '@/libs/breakpoints'
import imageProxy from '@/libs/image-proxy'

import auth from '@/mixins/auth'
import closeOnEsc from '@/mixins/close-on-esc'
import componentState from '@/mixins/component-state'
import projectRoleMixin from '@/mixins/projectRoleOptions'
import userTypes from '@/mixins/user-types'

import i18n from '@/i18n'
import debounce from 'lodash/debounce'
import state from '../../state'

Vue.directive('focus', {
  inserted: function (el) {
    el.focus()
  }
})

const newMember = {
  nameOrEmail: '',
  role: 'contributor',
  customRole: ''
}

const teamManagerView = 1
const bulkAddView = 2

export default {
  name: 'editMembersModal',
  mixins: [auth, closeOnEsc, componentState, projectRoleMixin, userTypes],
  props: ['project', 'members', 'addCallback', 'updateCallback', 'removeCallback', 'loading', 'hasMore'],
  components: { DropDown, InfiniteScroll },
  data: function () {
    return {
      active: false,
      numProcessing: 0,
      localMembers: [],
      membersToAdd: [],
      membersToRemove: [],
      dirtyMembers: [],
      returnedMembers: [],
      errorMessage: '',
      infoMessage: '',
      attemptClose: false,
      searchUsers: [],
      searchingAt: 0,
      saveInProgress: false,
      currentRole: '',
      dropdowns: {
        users: dropDownMixin.initDropdown()
      },
      usernameOrEmailPlaceholder: i18n.t('teamModal.usernameOrEmail'),
      customRolePlaceholder: i18n.t('teamModal.custom'),
      viewMode: teamManagerView,
      teamManagerView,
      bulkAddView,
      bulkAddEmails: '',
      groupOptions: [],
      memberOriginalGroups: new Map(),
      checkCurrentMember: [],
      canDeleteOwnAccount: false,
      canTeacherEditTeam: false,
      loggedInUserRole: ''
    }
  },
  watch: {
    active: function (isActive) {
      isActive ? this._callback('lockScrolling') : this._callback('unlockScrolling')
    }
  },
  computed: {
    isBulk: function () {
      return this.viewMode === bulkAddView
    },
    isTeamManager: function () {
      return this.viewMode === teamManagerView
    },
    isDirty: function () {
      return this.dirtyMembers.length > 0 || (this.membersToAdd.length > 0 && this.membersToAdd[0].nameOrEmail !== '') || this.membersToRemove.length > 0
    },
    processing: function () {
      return this.numProcessing > 0
    },
    isSaveDisabled: function () {
      return (!this.canEditRole(null) && !this.canDeleteOwnAccount && !this.isAdmin) || this.saveInProgress
    },
    hasUnsavedChanges: function () {
      return !this.errorMessage && this.active && this.isDirty
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    addAnotherRow: function () {
      return !this.isMobile ? i18n.t('teamModal.add') : !this.infoMessage ? i18n.t('teamModal.addMobile') : ''
    },
    addUsersOrSeeTeam: function () {
      return this.isBulk ? i18n.t('teamModal.teamManager') : i18n.t('teamModal.bulkUser')
    },
    autoOrVisible: function () {
      const isDropdownOverflow = this.localMembers.length + this.membersToAdd.length <= 3
      return isDropdownOverflow ? 'visible' : 'auto'
    },
    canEdit: function () {
      return this._get(state.project.permissions.edit, false) || this.getCurrentUserProjectRole !== 'resultsViewer'
    },
    hasMembershipEditPermissions: function () {
      return this.isAdmin || ['owner', 'co-owner', 'anonAdmin', 'admin', 'anonCoordinator', 'resultsViewer'].includes(this.getCurrentUserProjectRole)
    },
    getCurrentUserProjectRole: function () {
      const memberData = this.$store.getters.membership(this._get(state.project.id), this.currentUser.uuid)
      return memberData ? memberData.role : ''
    },
    canBulkAdd: function () {
      return (this.isBulk || this.isTeamManager) && this.hasMembershipEditPermissions
    },
    isJudgeTeamProject: function () {
      return this.project.displayType === 'judgeTeam'
    }
  },
  mounted: function () {
    if (this.isLoggedIn) {
      this.getCurrentUserMembership()
    }
  },
  methods: {
    teamMemberViewButtonClicked: function () {
      this.isTeamManager ? this.viewMode = bulkAddView : this.viewMode = teamManagerView
      this.errorMessage = ''
      this.infoMessage = ''
    },
    checkNameOrEmail: function (nameOrEmail) {
      if (nameOrEmail === '') {
        this.infoMessage = i18n.t('teamModal.addUser')
      } else {
        this.infoMessage = ''
      }
    },
    removeRow: function (index) {
      this.membersToAdd.splice(index, 1)
      this.infoMessage = ''
    },
    show: function () {
      this.localMembers = JSON.parse(JSON.stringify(this.members))
      if (!this.isAdmin) {
        if (this.getCurrentUserProjectRole !== 'resultsViewer') {
          this.roleOptions = this.roleOptions.filter(option => option.value.indexOf('anon') === -1)
        }
      }

      if (this.isJudgeTeamProject) {
        const groups = JSON.parse(JSON.stringify(this._get(state.project.judgeGroups, [])))
        groups.forEach((group) => {
          if (!this.groupOptions.includes(group.topic)) {
            this.groupOptions.push(group.topic)
          }
          group.users.forEach((user) => {
            this.localMembers.forEach((localMember) => {
              if (localMember.user.id === this.currentUser.id) {
                this.canDeleteOwnAccount = true
              }
              if (localMember.user.id === user.id) {
                localMember.group = group.topic
                // maintains the original data from load so we can tell if its been updated
                this.memberOriginalGroups.set(localMember.user.id, group.topic)
              }
            })
          })
        })
      }
      this.active = true
    },
    close: function () {
      if (this.hasUnsavedChanges && !this.attemptClose) {
        this.attemptClose = true
        return
      }
      this.confirmClose()
    },
    confirmClose: function () {
      this.active = false
      this.numProcessing = 0
      this.localMembers = null
      this.membersToAdd = []
      this.membersToRemove = []
      this.dirtyMembers = []
      this.returnedMembers = []
      this.errorMessage = ''
      this.searchUsers = []
      this.searchingAt = 0
      this.attemptClose = false
      this.$emit('close')
      this.$emit('getMembersCount')
      this.infoMessage = ''
    },
    updateRole: function (member) {
      const original = this.members.filter(function (m) { return m.user.uuid === member.user.uuid }).pop()
      member.dirty = member.role !== original.role
      if (member.dirty) {
        this.dirtyMembers.push(member.user.id)
      } else {
        const index = this.dirtyMembers.indexOf(member.user.id)
        if (index > -1) {
          this.dirtyMembers.splice(index, 1)
        }
      }
    },
    updateCustomRole: function (member) {
      const original = this.members.filter(function (m) { return m.user.uuid === member.user.uuid }).pop()
      member.dirty = member.customRole !== original.customRole
      if (member.dirty) {
        this.dirtyMembers.forEach((dirtyMember) => {
          if (member.user.id !== dirtyMember) {
            this.dirtyMembers.push(member.user.id)
          }
        })
      } else {
        const index = this.dirtyMembers.indexOf(member.user.id)
        if (index > -1) {
          this.dirtyMembers.splice(index, 1)
        }
      }
    },
    add: debounce(function () {
      this.checkForMatch()
      this.errorMessage = ''
      this.infoMessage = i18n.t('teamModal.addUser')
      this.membersToAdd.push(Object.assign({}, newMember))
    }, 150),
    processRemove: function () {
      this.membersToRemove.forEach((member, index) => {
        this.$store.dispatch('removeMember', { project: this.project, uuid: member.user.uuid }).then(() => {
          this.removeCallback(member)
          this.membersToRemove.shift()
          this.numProcessing--
          if (this.membersToRemove.length === 0 && this.membersToAdd.length > 0) {
            this.processAdd()
          }
          if (this.numProcessing === 0) {
            this.close()
          }
        }, (error) => {
          this.errorMessage = error
          this.infoMessage = ''
          this.numProcessing = 0
        })
      })
    },
    processAdd: function () {
      this.membersToAdd.forEach((newMember, index) => {
        if (newMember.nameOrEmail !== '') {
          const data = { project: this.project, lookup: newMember.nameOrEmail, role: newMember.role, customRole: newMember.customRole, invitedByUserRole: this.loggedInUserRole }
          this.$store.dispatch('inviteMember', data).then((member) => {
            this.addCallback(member)
            this.membersToAdd.shift()
            this.numProcessing--
            if (this.numProcessing === 0) {
              this.close()
            }
          }, () => {
            this.errorMessage = 'Unable to create account for ' + newMember.nameOrEmail
            this.infoMessage = ''
            this.numProcessing = 0
          })
        } else {
          this.membersToAdd.splice(index, 1)
          this.numProcessing--
          if (this.numProcessing === 0) {
            this.close()
          }
        }
      })
    },
    checkSaveChanges: debounce(function () {
      this.saveInProgress = true
      if (this.isJudgeTeamProject) {
        if (this.isDirty) {
          this.processRemove()
          this.infoMessage = 'Successfully updated judge group'
        }
        this.localMembers.forEach((member) => {
          if (member.dirty) {
            const data = {
              topic: member.group,
              userName: member.user.userName,
              projectID: this.project.id
            }
            this.$store.dispatch('updateUserJudgeGroup', data).then(() => {
              this.infoMessage = 'Successfully updated judge group'
              this.saveInProgress = false
            }, () => {
              this.errorMessage = 'Failed to update judge group'
              this.saveInProgress = false
            })
          }
        })
      } else {
        this.returnedMembers = []
        this.errorMessage = ''
        this.infoMessage = ''
        if (this.isBulk) {
          this.$store.dispatch('bulkInviteToProject', {
            project: this.project,
            emails: this.bulkAddEmails
          }).then((newMembers) => {
            this.bulkAddEmails = ''
            newMembers.forEach((member) => {
              this.addCallback(member)
              // all update local array to trigger UI update
              this.localMembers.push(member)
            })
            this.infoMessage = 'Successfully invited users'
            this.saveInProgress = false
          }, (err) => {
            this.errorMessage = err
            this.saveInProgress = false
          })
        } else {
          this.inviteAndSave()
          this.saveInProgress = false
        }
      }
    }),
    inviteAndSave: function () {
      this.checkForMatch()

      this.numProcessing = this.localMembers.length + this.membersToAdd.length + this.membersToRemove.length

      this.processRemove()

      if (this.membersToRemove.length === 0) {
        this.processAdd()
      }

      this.localMembers.forEach((updateMember) => {
        if (updateMember.dirty) {
          this.$store.dispatch('updateMember', { project: this.project, uuid: updateMember.user.uuid, role: updateMember.role, customRole: updateMember.customRole, invitedByUserRole: this.loggedInUserRole }).then((updatedMember) => {
            updateMember.dirty = false
            const index = this.dirtyMembers.indexOf(updateMember.id)
            this.dirtyMembers.splice(index, 1)
            this.updateCallback(updatedMember)
            this.numProcessing--
            if (this.numProcessing === 0) {
              this.close()
            }
          }, () => {
            this.errorMessage = 'Failed to save ' + updateMember.nameOrEmail + '\'s membership role'
            this.infoMessage = ''
            this.numProcessing = 0
          })
        } else {
          this.numProcessing--
          if (this.numProcessing === 0) {
            this.close()
          }
        }
      })
    },
    remove: function (member, index) {
      this.localMembers.splice(index, 1)
      this.membersToRemove.push(member)
      this.infoMessage = ''
    },
    removeAddedMember: function (index) {
      this.membersToAdd.splice(index, 1)
      this.errorMessage = ''
      this.infoMessage = ''
    },
    thumbnail: imageProxy.thumbnail,
    searchForUsers: function (searchName, index) {
      const search = searchName.toLowerCase()

      if (searchName.length < 3) {
        this.searchUsers = []
        return
      }
      this.searchingAt = index
      this.$store.dispatch('getAllLikeName', { query: search, limit: 10 }).then((response) => {
        this.searchUsers = response.users
          .filter((u) => { return u.uuid !== this.currentUser.uuid })
          .sort((x, y) => { return x.userName.toLowerCase() === search ? -1 : y.userName.toLowerCase() === search ? 1 : 0 })

        this.dropdowns.users.show = true
      })
    },
    selectUser: function (user) {
      this.searchUsers = []

      let userInConversation = false
      this.localMembers.forEach((foundUser) => {
        if (user.id === foundUser.user.id) {
          this.infoMessage = user.userName + ' already in team'
          userInConversation = true
          this.membersToAdd.splice(this.searchingAt, 1)
        }
      })
      this.membersToAdd.forEach((foundUser) => {
        if (user.id === foundUser?.user?.id) {
          this.infoMessage = user.userName + ' already in team'
          userInConversation = true
          this.membersToAdd.splice(this.searchingAt, 1)
        }
      })

      if (!userInConversation) {
        this.membersToAdd[this.searchingAt].nameOrEmail = user.userName
        this.membersToAdd[this.searchingAt].user = user
      }

      this.searchingAt = 0
    },
    checkForMatch: function () {
      this.searchUsers.forEach(user => {
        if (user.userName.toLowerCase() === this.membersToAdd[this.searchingAt].nameOrEmail.toLowerCase()) {
          this.selectUser(user)
        } else {
          this.searchUsers = []
        }
      })
    },
    fetchMembers: function () {
      this.$emit('fetchMembers')
    },
    canEditRole: function (member) {
      if (member && (member.user.id === this.currentUser.id)) {
        this.canDeleteOwnAccount = true
      } else if (this.isAdmin) {
        this.canDeleteOwnAccount = true
      }
      return (!member || member.role !== 'owner') && ((!this.processing && this.hasMembershipEditPermissions && this.canDeleteOwnAccount) || this.canTeacherEditTeam)
    },
    canEditGroup: function (member) {
      return !this.processing && (!member || member.role === 'contributor') && this.currentUser.isJudgingAdmin
    },
    updateGroup: function (member) {
      const memberOriginalGroup = this.memberOriginalGroups.get(member.user.id)
      member.dirty = member.group !== memberOriginalGroup
    },
    getCurrentUserMembership: function () {
      this.canTeacherEditTeam = false
      return this.$store.dispatch('getMembership', { project: this.project, user: this.currentUser }).then((membership) => {
        if (membership && membership.role) {
          this.loggedInUserRole = membership.role
          if ((membership && membership.role === 'anonAdmin' && this.currentUser.type?.includes(this.UserTypeTeacher)) && (this.project.user.type === this.UserTypeStudent)) {
            this.canTeacherEditTeam = true
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  :root {
    --overflow-value: visible; // CSS variable to prevent dropdown from being cut and allow modal to scroll
  }

  .modal-card {
    cursor: default;
    overflow: visible; // overriding Bulma style for the user dropdown
    border-radius: 6px;
  }

  .modal-background {
    cursor: default;
  }

  .modal-card-title {
    cursor: text;
  }

  .loader.is-centered {
    font-size: 2rem;
    margin: 1rem auto;
  }

  .modal-card-body {
    justify-content: space-between;
    overflow: var(--overflow-value); // overriding Bulma style for the user dropdown
  }

  .avatar {
    float: left;
    width: 2rem;
    height: 2rem;
    margin-top: 0.25rem;
    border-radius: 50%;
    overflow: hidden;
    img {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .disabled-input {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    opacity: 0;
    cursor: not-allowed;
    select {
      padding: 30px;
    }
  }
  .hide {
    display: none;
  }

  .input-container {
    position: relative;
  }

  .label-container {
    padding-right: 0;
    .label {
      margin-left: 1.5rem;
      margin-top: 0.5rem;
      // Responsive Font-Size (relative to viewport width)
      font-size: clamp(0.75rem, 5vw , 1rem);
      // Line-Clamp
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
      cursor: text;

      @include mobile {
        margin-left: 0;
      }
    }
  }
  .select-user-dropdown {
    bottom: 40px;
  }

  .info-message {
    font-size: clamp(0.5rem, 3.5vw , 1rem);
  }

  .confirm-close {
    color: $danger;
    font-size: 0.75rem;
    margin: 0 0.5rem;
  }

  .select {
    height: auto !important;
    select {
      width: 100%;
    }
  }

  .input-container {
    padding: 0;
    align-items: center;
    .input {
      width: 95%;
    }
  }

  .empty-block {
    width: 0px;
  }

  .remove, .bulk-add {
    flex-grow: 0;
    background: none;
    align-items: center;
    border: none;
    font-size: 1rem;
    i {
      color: $primary;
    }
  }

  .bulk-add {
    color: #373737;
    font-weight: 500;
    margin-left: 0.25rem;
    margin-top: 0.125rem;
  }

  .footer-buttons {
    justify-content: space-between;
  }
  .button {
    border-radius: 0;
  }

  .footer-add-button {
    align-items: center;
    .add-button {
      background: none;
      border: none;
      font-size: 2rem;
      color: $primary;
      padding: 0;
    }
    .add-button-label {
      font-weight: 500;
      padding: 0.5rem;
      cursor: pointer;
    }
  }

  .inline-padding {
    padding-left: 0.6rem;
    margin-top: 0;
  }

  i {
    cursor: pointer;
  }

  .visually-hidden:not(:focus):not(:active) {
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px 1px 1px 1px);
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
  }

  .team-member .has-text-left {
    word-break: break-word;
  }
  .adjust-margin {
    margin-left: 16rem;
    @include mobile {
    margin-left: 8rem;
  }
  }
</style>
